(() => {
  const sectionSelector = '[data-bookverse-series-carousel]';
  const configSelector = '[data-bookverse-series-config]';
  const trackSelector = '[data-carousel-track]';
  const itemSelector = '[data-bookverse-series-result-item]';
  const moreCardSelector = '[data-bookverse-series-more-card]';
  const initializedAttribute = 'data-bookverse-series-initialized';
  const pendingAttribute = 'data-bookverse-series-pending';
  const loadedAttribute = 'data-bookverse-series-loaded';
  const deferredAttribute = 'data-carousel-deferred';
  const postPaintReadyAttribute = 'data-carousel-post-paint-ready';
  const postPaintReadyEvent = 'carousel:post-paint-ready';

  const cache = new Map();

  const parseConfig = (section) => {
    const script = section.querySelector(configSelector);
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  };

  const fetchResults = async (url) => {
    if (!url) return [];

    if (cache.has(url)) {
      return cache.get(url);
    }

    const request = fetch(url, { headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error('BookVerse series request failed');
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.from(doc.querySelectorAll(itemSelector));
      })
      .catch(() => []);

    cache.set(url, request);
    return request;
  };

  const relationshipPriority = (relationship) => {
    const normalized = String(relationship || '').toLowerCase().trim();

    if (normalized === 'ordered') return 0;
    if (normalized === 'companion') return 2;

    return 1;
  };

  const parseSeriesNumber = (item) => {
    const number = Number.parseFloat(item.dataset.seriesNumber);
    return Number.isFinite(number) ? number : null;
  };

  const isAvailable = (item) => item.dataset.available === 'true';

  const productKey = (item) =>
    item.dataset.productId || item.dataset.productHandle || item.dataset.productUrl || '';

  const dedupeItems = (items) => {
    const seen = new Set();

    return items.filter((item) => {
      const key = productKey(item);
      if (!key) return true;
      if (seen.has(key)) return false;

      seen.add(key);
      return true;
    });
  };

  const groupByWorkId = (items, currentWorkId) => {
    const groups = new Map();

    items.forEach((item) => {
      const workId = item.dataset.workId;

      if (!workId) return;
      if (currentWorkId && workId === currentWorkId) return;

      if (!groups.has(workId)) {
        groups.set(workId, []);
      }

      groups.get(workId).push(item);
    });

    return groups;
  };

  const chooseRepresentative = (items) => {
    return items.find(isAvailable) || items[0];
  };

  const sortRepresentatives = (items) =>
    [...items].sort((a, b) => {
      const relationshipDifference =
        relationshipPriority(a.dataset.seriesRelationship) -
        relationshipPriority(b.dataset.seriesRelationship);

      if (relationshipDifference !== 0) return relationshipDifference;

      const aSeriesNumber = parseSeriesNumber(a);
      const bSeriesNumber = parseSeriesNumber(b);
      const aHasSeriesNumber = aSeriesNumber !== null;
      const bHasSeriesNumber = bSeriesNumber !== null;

      if (aHasSeriesNumber !== bHasSeriesNumber) return aHasSeriesNumber ? -1 : 1;
      if (aHasSeriesNumber && bHasSeriesNumber && aSeriesNumber !== bSeriesNumber) {
        return aSeriesNumber - bSeriesNumber;
      }

      return String(a.dataset.productTitle || '').localeCompare(String(b.dataset.productTitle || ''));
    });

  const hideSection = (section) => {
    section.removeAttribute(pendingAttribute);
    section.style.removeProperty('visibility');
    section.style.removeProperty('min-height');
    section.hidden = true;
    section.dataset.carouselAvailability = 'empty';
    section.dispatchEvent(new CustomEvent('carousel:availability-changed', { bubbles: true }));
  };

  const showSection = (section) => {
    section.removeAttribute(pendingAttribute);
    section.style.removeProperty('visibility');
    section.style.removeProperty('min-height');
    section.hidden = false;
    section.dataset.carouselAvailability = 'ready';
    section.dispatchEvent(new CustomEvent('carousel:availability-changed', { bubbles: true }));
  };

  const revealCurrentItem = (track) => {
    const currentItem = track.querySelector('[data-current-series-card]');
    if (!currentItem) return;

    window.requestAnimationFrame(() => {
      const trackRect = track.getBoundingClientRect();
      const itemRect = currentItem.getBoundingClientRect();
      const itemStart = itemRect.left - trackRect.left + track.scrollLeft;
      const itemEnd = itemStart + itemRect.width;
      const viewportStart = track.scrollLeft;
      const viewportEnd = viewportStart + track.clientWidth;
      let targetScrollLeft = viewportStart;

      if (itemStart < viewportStart) {
        targetScrollLeft = itemStart;
      } else if (itemEnd > viewportEnd) {
        targetScrollLeft = itemEnd - track.clientWidth;
      }

      const maxScrollLeft = Math.max(track.scrollWidth - track.clientWidth, 0);
      targetScrollLeft = Math.min(Math.max(targetScrollLeft, 0), maxScrollLeft);

      if (Math.abs(targetScrollLeft - viewportStart) > 1) {
        track.scrollTo({ left: targetScrollLeft, behavior: 'auto' });
      }
    });
  };

  const populateSection = async (section) => {
    if (section.hasAttribute(loadedAttribute)) return;
    section.setAttribute(loadedAttribute, 'true');

    const config = parseConfig(section);
    const track = section.querySelector(trackSelector);
    const moreCard = track ? track.querySelector(moreCardSelector) : null;

    if (!config || !track || !config.fetchUrl) {
      hideSection(section);
      return;
    }

    if (moreCard) {
      moreCard.hidden = true;
    }

    const showUnavailable = config.showUnavailable !== false;
    const currentCard = track.querySelector('[data-current-series-card]');
    const currentItem = currentCard && (showUnavailable || isAvailable(currentCard)) ? currentCard : null;
    const fetchedItems = dedupeItems(await fetchResults(config.fetchUrl));
    const groups = groupByWorkId(fetchedItems, config.currentWorkId);
    const fetchedRepresentatives = Array.from(groups.values())
      .map(chooseRepresentative)
      .filter((item) => item && (showUnavailable || isAvailable(item)));

    if (fetchedRepresentatives.length === 0) {
      hideSection(section);
      return;
    }

    const representatives = currentItem
      ? [currentItem, ...fetchedRepresentatives]
      : fetchedRepresentatives;
    const productLimit = Number(config.productLimit || 10);
    const requiredProductCount = Number(config.requiredProductCount || 1);
    const sortedRepresentatives = sortRepresentatives(representatives);

    if (sortedRepresentatives.length < requiredProductCount) {
      hideSection(section);
      return;
    }

    const hasMoreProducts = sortedRepresentatives.length > productLimit;
    let limitedRepresentatives = sortedRepresentatives.slice(0, productLimit);

    if (currentItem && !limitedRepresentatives.includes(currentItem)) {
      limitedRepresentatives = sortRepresentatives([
        ...limitedRepresentatives.slice(0, Math.max(productLimit - 1, 0)),
        currentItem
      ]);
    }

    const visibleItems = limitedRepresentatives.map((item) => item.cloneNode(true));

    track.querySelectorAll(itemSelector).forEach((item) => item.remove());
    visibleItems.forEach((item) => track.appendChild(item));

    if (moreCard && hasMoreProducts) {
      moreCard.hidden = false;
      track.appendChild(moreCard);
    }

    showSection(section);
    section.dispatchEvent(new CustomEvent('carousel:content-updated', { bubbles: true }));
    revealCurrentItem(track);
  };

  const populateSectionAfterFirstPaint = (section) => {
    if (window.Shopify?.designMode || document.documentElement.hasAttribute(postPaintReadyAttribute)) {
      populateSection(section);
      return;
    }

    if (section.hidden) {
      section.hidden = false;
      section.setAttribute(pendingAttribute, 'true');
      section.style.visibility = 'hidden';
      section.style.minHeight = '1px';
    }

    document.addEventListener(postPaintReadyEvent, () => populateSection(section), { once: true });
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll(sectionSelector).forEach((section) => {
      if (section.hasAttribute(deferredAttribute)) return;
      if (section.hasAttribute(initializedAttribute)) return;

      section.setAttribute(initializedAttribute, 'true');
      populateSectionAfterFirstPaint(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  document.addEventListener('carousel:fallback-activate', (event) => {
    event.target.querySelectorAll(sectionSelector).forEach((section) => {
      section.removeAttribute(deferredAttribute);
      populateSection(section);
    });
  });
})();
